import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, Sun, Moon, LogOut, Bell, UserPlus, Mic, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { useThemeMode } from '@/context/ThemeContext';
import { cn } from '@/lib/utils';

const PAGE_TITLES: Record<string, string> = {
    '/dashboard': 'Station',
    '/handover': 'Übergabe',
    '/residents': 'Bewohnerliste',
    '/sis': 'SIS-Dokumentation',
    '/wounds': 'Wundmanagement',
    '/medications': 'Medikamente',
    '/admissions': 'Aufnahmen',
    '/occupancy': 'Belegung',
    '/inventory': 'Lager',
    '/schedule': 'Dienstplan',
    '/inbox': 'Postfach',
    '/contacts': 'Kontakte',
    '/settings': 'Einstellungen',
};

const DYNAMIC_PAGE_TITLES: Array<{ matches: (pathname: string) => boolean; title: string }> = [
    { matches: (pathname) => /^\/residents\/[^/]+$/.test(pathname), title: 'Bewohnerprofil' },
    { matches: (pathname) => pathname === '/wounds/approval', title: 'Wundfreigaben' },
    { matches: (pathname) => /^\/wounds\/[^/]+$/.test(pathname), title: 'Wunddetail' },
];

const QUICK_ACTIONS = [
    { icon: UserPlus, label: 'Neue Aufnahme', to: '/admissions/new' },
    { icon: Mic, label: 'SIS-Doku', to: '/sis/new' },
    { icon: ArrowLeftRight, label: 'Übergabe', to: '/handover' },
];

interface TopbarProps {
    onMenuToggle: () => void;
}

export function Topbar({ onMenuToggle }: TopbarProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const { logout } = useAuth();
    const { isDark, setMode } = useThemeMode();

    const dynamicTitle = DYNAMIC_PAGE_TITLES.find(({ matches }) => matches(location.pathname))?.title;

    // Find matching page title (longest prefix match)
    const title = dynamicTitle ?? Object.entries(PAGE_TITLES)
        .filter(([path]) => location.pathname === path || location.pathname.startsWith(path + '/'))
        .sort(([a], [b]) => b.length - a.length)[0]?.[1] ?? 'MediFlow Pflege';

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <header className="flex h-16 items-center gap-4 border-b px-6 shrink-0" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-default)' }}>
            {/* Menu toggle (mobile) */}
            <button
                onClick={onMenuToggle}
                className="lg:hidden rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
                <Menu className="h-5 w-5" />
            </button>

            {/* Page title */}
            <motion.h1
                key={title}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-lg font-bold text-neutral-900 dark:text-neutral-100"
            >
                {title}
            </motion.h1>

            <div className="flex-1" />

            {/* Quick Actions */}
            <div className="hidden md:flex items-center gap-1.5">
                {QUICK_ACTIONS.map(({ icon: Icon, label, to }) => (
                    <Button
                        key={to}
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(to)}
                        className="gap-1.5 text-xs"
                    >
                        <Icon className="h-3.5 w-3.5" />
                        {label}
                    </Button>
                ))}
            </div>

            {/* Notifications */}
            <button className="relative rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                <Bell className="h-5 w-5" />
                {/* Unread dot */}
                <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-brand-500" />
            </button>

            {/* Theme toggle */}
            <button
                onClick={() => setMode(isDark ? 'light' : 'dark')}
                className={cn(
                    'rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors',
                )}
                title={isDark ? 'Hell-Modus' : 'Dunkel-Modus'}
            >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            {/* Logout */}
            <button
                onClick={() => void handleLogout()}
                className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title="Abmelden"
            >
                <LogOut className="h-5 w-5" />
            </button>
        </header>
    );
}
